import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'
import { makeStyles, Fab, Zoom } from '@material-ui/core'
import { AttachmentProvider, useAttachment } from '../contexts/AttachmentContext'
import { AttachmentViewer } from '../Attachments/AttachmentViewer'
import Icon from '../../../../components/atoms/Icon'
import { useAIThread, useInvokeDenaliAssistant, useRetryMessage, useCancelDenaliAssistant } from '../../../../api/denaliAssistant'
import MessageInput from '../common/MessageInput'
import Background from '../../../../providers/BackgroundProvider/Background'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import DenaliLoader from '../common/DenaliLoader'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import MessageList from './MessageList'
import ThreadFocusHeader from './ThreadFocusHeader'
import { useScrollBehavior, ScrollEvent } from './useScrollBehavior'

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row', // Changed to row for side-by-side layout
    overflow: 'hidden' // Prevent the container itself from scrolling
  },

  chatWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
    position: 'relative',
    transition: 'flex 0.3s ease'
  },

  messagesWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    overflowY: 'auto',
    position: 'relative',
    width: '100%'
  },
  messageContent: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: 'var(--padding-md)',
    boxSizing: 'border-box'
  },
  inputSection: {
    flexShrink: 0,
    padding: 'var(--padding-md)',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box'
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: '80px',
    right: '20px',
    backgroundColor: 'var(--surface-bg-color)',
    color: 'var(--color-primary-500)',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d1)'
    }
  }
}))

function ChatLayout ({
  classes, isNewThread, threadFocus, threadName, threadId, isScrolled,
  messagesWrapperRef, parentInfo, messages, handleRetry, retryMutation,
  messageInputRef, setPendingSkillId, messagesEndRef, isAtBottom,
  scrollToBottom, handleSendMessage, handleCancel, isCancelling, isProcessing
}) {
  const { isOpen, effectivePreset, customRatio } = useAttachment()
  const chatFlex = isOpen ? effectivePreset.chatFlex : 1
  const chatHidden = isOpen && chatFlex === 0

  return (
    <div className={classes.chatContainer}>
      <div
        className={classes.chatWrapper}
        style={{
          flex: chatFlex,
          transition: customRatio ? 'none' : undefined,
          ...(chatHidden && { overflow: 'hidden', minWidth: 0, maxWidth: 0, padding: 0 })
        }}
      >
        {!chatHidden && (
          <>
            {!isNewThread && (
              <ThreadFocusHeader
                focus={threadFocus}
                threadName={threadName}
                threadId={threadId}
                isScrolled={isScrolled}
              />
            )}
            <div ref={messagesWrapperRef} className={classes.messagesWrapper}>
              <div className={classes.messageContent}>
                <MessageList
                  parentInfo={parentInfo}
                  messages={messages}
                  isNewThread={isNewThread}
                  onRetry={handleRetry}
                  isRetrying={retryMutation.isLoading}
                  onSuggestionClick={(prompt, opts) => {
                    messageInputRef.current?.setValue(prompt)
                    setPendingSkillId(opts?.skillId || null)
                  }}
                  onReply={(selectedText) => {
                    messageInputRef.current?.addTextAttachment(selectedText, 'selected-text.txt')
                  }}
                />
                <div ref={messagesEndRef} style={{ height: '1px' }} />
              </div>
            </div>
            <Zoom in={!isAtBottom}>
              <Fab
                className={classes.scrollToBottomButton}
                size='small'
                onClick={scrollToBottom}
                style={{ zIndex: '1000' }}
                aria-label='Scroll to bottom'
              >
                <Icon name='chevronDown' />
              </Fab>
            </Zoom>
            <div className={classes.inputSection}>
              <MessageInput
                ref={messageInputRef}
                onSend={handleSendMessage}
                onCancel={handleCancel}
                disabled={isProcessing}
                isProcessing={isProcessing}
                isCancelling={isCancelling}
                placeholder={isNewThread ? 'Start a new conversation...' : 'Type a message...'}
                submissionMode='external'
              />
            </div>
          </>
        )}
      </div>
      <AttachmentViewer />
    </div>
  )
}

ChatLayout.propTypes = {
  classes: PropTypes.object.isRequired,
  isNewThread: PropTypes.bool,
  threadFocus: PropTypes.object,
  threadName: PropTypes.string,
  threadId: PropTypes.string,
  isScrolled: PropTypes.bool,
  messagesWrapperRef: PropTypes.object,
  parentInfo: PropTypes.object,
  messages: PropTypes.array,
  handleRetry: PropTypes.func,
  retryMutation: PropTypes.object,
  messageInputRef: PropTypes.object,
  setPendingSkillId: PropTypes.func,
  messagesEndRef: PropTypes.object,
  isAtBottom: PropTypes.bool,
  scrollToBottom: PropTypes.func,
  handleSendMessage: PropTypes.func,
  handleCancel: PropTypes.func,
  isCancelling: PropTypes.bool,
  isProcessing: PropTypes.bool
}

function ChatInterface () {
  const classes = useStyles()
  const { threadId } = useParams()
  const history = useHistory()
  const messagesEndRef = useRef(null)
  const messagesWrapperRef = useRef(null)
  const messageInputRef = useRef(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [isScrolled, setIsScrolled] = useState(false)
  const [pendingSkillId, setPendingSkillId] = useState(null)

  // Track previous generating message ID to detect start/finish transitions
  const prevGeneratingMessageIdRef = useRef(null)

  const isNewThread = threadId === 'new'

  // Fetch thread data
  const { data: threadData, isLoading, isSuccess } = useAIThread(threadId, {
    enabled: !isNewThread
  })

  // Mutations
  const invokeMutation = useInvokeDenaliAssistant()
  const retryMutation = useRetryMessage()
  const cancelMutation = useCancelDenaliAssistant()
  const cancelledDuringInvokeRef = useRef(false)
  const [isCancelling, setIsCancelling] = useState(false)

  // Get messages from thread data
  const messages = threadData?.thread?.messages || []

  // Get focus from thread metadata context
  const threadFocus = threadData?.thread?.metadata?.context?.focus || null

  // Get thread name for header display
  const threadName = threadData?.thread?.name || null

  // Find the generating message (if any)
  const generatingMessage = messages.find(m => m.isGenerating)
  const generatingMessageId = generatingMessage?.threadMessageId || null

  // Check if AI is currently processing/generating
  const isProcessing = invokeMutation.isLoading || !!generatingMessage

  // Initialize scroll behavior manager
  const {
    handleEvent,
    handleUserScroll,
    resetAttention,
    checkIsAtBottom
  } = useScrollBehavior(messagesWrapperRef)

  // Check if user is at bottom of scroll
  const checkIfAtBottom = useCallback(() => {
    const isBottom = checkIsAtBottom(50)
    setIsAtBottom(isBottom)
  }, [checkIsAtBottom])

  // Scroll to bottom function (for the FAB button)
  const scrollToBottom = useCallback(() => {
    handleEvent(ScrollEvent.USER_SUBMITTED) // Reuse the scroll-to-bottom action
    setIsAtBottom(true)
  }, [handleEvent])

  // Monitor scroll position and handle user scroll during generation
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return

    const onScroll = () => {
      checkIfAtBottom()
      handleUserScroll(isProcessing)
    }

    wrapper.addEventListener('scroll', onScroll)
    return () => wrapper.removeEventListener('scroll', onScroll)
  }, [checkIfAtBottom, handleUserScroll, isProcessing])

  // Track scroll position for header styling (separate effect for stability)
  // useLayoutEffect ensures the effect runs synchronously after DOM mutations
  useLayoutEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return

    const onScrollForHeader = () => {
      const scrollTop = wrapper.scrollTop || 0
      setIsScrolled(scrollTop > 0)
    }

    // Set initial state
    onScrollForHeader()

    wrapper.addEventListener('scroll', onScrollForHeader)
    return () => wrapper.removeEventListener('scroll', onScrollForHeader)
  }, [isSuccess]) // Re-run when data loads

  // Handle conversation loaded event — fire once on initial load only
  const hasScrolledToLoadRef = useRef(false)
  useEffect(() => {
    if (!isNewThread && isSuccess && messages.length > 0 && !hasScrolledToLoadRef.current) {
      hasScrolledToLoadRef.current = true
      setTimeout(() => {
        handleEvent(ScrollEvent.CONVERSATION_LOADED)
      }, 100)
    }
  }, [isNewThread, isSuccess, messages.length, handleEvent])

  // Detect generation start/finish transitions
  useEffect(() => {
    const prevId = prevGeneratingMessageIdRef.current
    const currentId = generatingMessageId

    // Generation started: no previous ID, now we have one
    if (!prevId && currentId) {
      resetAttention()
      handleEvent(ScrollEvent.GENERATION_STARTED)
    }

    // Generation finished: had an ID, now we don't
    if (prevId && !currentId) {
      handleEvent(ScrollEvent.GENERATION_FINISHED)
      setIsCancelling(false)
    }

    // Update the ref for next comparison
    prevGeneratingMessageIdRef.current = currentId
  }, [generatingMessageId, handleEvent, resetAttention])

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim()) return

    // Fire the user submitted event immediately
    handleEvent(ScrollEvent.USER_SUBMITTED)

    try {
      const context = {
        currentView: 'ai-assistant-chat',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      if (pendingSkillId) {
        context.skillId = pendingSkillId
      }

      const result = await invokeMutation.mutateAsync({
        threadId: isNewThread ? undefined : threadId,
        prompt: message,
        context,
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        },
        attachments
      })

      setPendingSkillId(null)

      if (cancelledDuringInvokeRef.current) {
        cancelledDuringInvokeRef.current = false
        messageInputRef.current?.clear()
        if (result.threadId) {
          await cancelMutation.mutateAsync({ threadId: result.threadId })
          if (isNewThread) {
            history.replace(`/admin/denali/analysis/assistant/chat/${result.threadId}`)
          }
        }
        return
      }

      // If this was a new thread, navigate first (component unmounts, no need to clear)
      if (isNewThread && result.threadId) {
        history.replace(`/admin/denali/analysis/assistant/chat/${result.threadId}`)
        return
      }

      messageInputRef.current?.clear()
      setIsAtBottom(true)
    } catch (error) {
      console.error('Failed to send message:', error)
      // Clear sending state even on error
      messageInputRef.current?.clear()
      setPendingSkillId(null)
      // TODO: Show error toast
    }
  }, [threadId, isNewThread, invokeMutation, cancelMutation, history, handleEvent, pendingSkillId])

  const handleCancel = useCallback(async () => {
    if (invokeMutation.isLoading) {
      cancelledDuringInvokeRef.current = true
      setIsCancelling(true)
      return
    }
    if (!threadId || isNewThread) return
    setIsCancelling(true)
    try {
      await cancelMutation.mutateAsync({ threadId })
    } catch (error) {
      console.error('Failed to cancel:', error)
      setIsCancelling(false)
    }
  }, [threadId, isNewThread, invokeMutation.isLoading, cancelMutation])

  const handleRetry = useCallback(async (message) => {
    if (!threadId || !message.threadMessageId) return

    // Fire the user submitted event (retry is similar to submit)
    handleEvent(ScrollEvent.USER_SUBMITTED)

    try {
      await retryMutation.mutateAsync({
        threadId,
        threadMessageId: message.threadMessageId
      })
      // The mutation will invalidate the query cache, which will refetch and show updated messages
      setIsAtBottom(true)
    } catch (error) {
      console.error('Failed to retry message:', error)
      // TODO: Show error toast
    }
  }, [threadId, retryMutation, handleEvent])

  const parentInfo = useMemo(() => {
    return {
      parentType: 'thread',
      parentId: threadId
    }
  }, [threadId])

  if (!isNewThread && isLoading) {
    return (
      <div className={classes.chatContainer}>
        <FlexColumn className='gap-md items-center justify-center w-full' style={{ padding: '40px' }}>
          <DenaliLoader />
          <FadeIn style={{ color: 'var(--dai-color-text-dim)' }} delay={1} duration={2}>Loading Conversation</FadeIn>
        </FlexColumn>
      </div>
    )
  }

  return (
    <Background
      light='var(--dai-bg)'
      dark='var(--dai-bg)'
    >
      <AttachmentProvider threadId={threadId} focus={threadFocus}>
        <ChatLayout
          classes={classes}
          isNewThread={isNewThread}
          threadFocus={threadFocus}
          threadName={threadName}
          threadId={threadId}
          isScrolled={isScrolled}
          messagesWrapperRef={messagesWrapperRef}
          parentInfo={parentInfo}
          messages={messages}
          handleRetry={handleRetry}
          retryMutation={retryMutation}
          messageInputRef={messageInputRef}
          setPendingSkillId={setPendingSkillId}
          messagesEndRef={messagesEndRef}
          isAtBottom={isAtBottom}
          scrollToBottom={scrollToBottom}
          handleSendMessage={handleSendMessage}
          handleCancel={handleCancel}
          isCancelling={isCancelling}
          isProcessing={isProcessing}
        />
      </AttachmentProvider>
    </Background>
  )
}

export default ChatInterface
